import React, { useMemo, useCallback } from 'react';
import { ReactGrid, CellChange, TextCell, Id } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import { Box } from '@mui/material';
import {
    AssignmentRow,
    DEFAULT_ASSIGNMENT_LIST,
    buildAssignmentColumns,
    buildAssignmentRows,
} from './insurerAssignmentGridModel';
import { ACTION_COLUMN_ID, gridContainerSx, useColumnWidths, useEditableGrid } from './editableGrid';

/**
 * SHER-1128 (SHER-308 Phase 4) — the Billing assignment tab's grid (Decision A/B).
 *
 * One row per insurer: code | name | price list. Existing insurers show code and name
 * read-only (name is display — they are not renamed here) and let you reassign the price
 * list. The ➕ footer adds a new insurer row where code and name ARE editable, so Save can
 * create a named insurer (via the existing addInsuranceToInvestigation).
 *
 * Presentational and self-contained via useEditableGrid so the catalog is interactive; the
 * Billing route wires Save/Export/Import against assignments.csv.
 */
export interface InsurerAssignmentGridProps {
    rows: AssignmentRow[];
    onRowsChange?: (rows: AssignmentRow[]) => void;
}

const EDITABLE_TEXT_FIELDS = ['insuranceCode', 'name', 'priceList'] as const;

const makeNewRow = (): AssignmentRow => ({
    insuranceCode: '', name: '', priceList: DEFAULT_ASSIGNMENT_LIST, isNew: true,
});

// An untouched new insurer row is removed on an action-cell click; existing rows toggle DELETE.
const isRowEmpty = (row: AssignmentRow): boolean => !!row.isNew && !row.insuranceCode?.trim() && !row.name?.trim();

const InsurerAssignmentGrid: React.FC<InsurerAssignmentGridProps> = ({ rows, onRowsChange }) => {
    const { columnWidths, handleColumnResize } = useColumnWidths();
    const { gridRows, commitRows, onActionFocus } = useEditableGrid<AssignmentRow>(rows, onRowsChange, { makeNewRow, isRowEmpty });

    const columns = useMemo(() => buildAssignmentColumns(columnWidths), [columnWidths]);
    const gridModel = useMemo(() => buildAssignmentRows(gridRows), [gridRows]);

    const handleChanges = useCallback((changes: CellChange[]) => {
        const next = gridRows.map((r) => ({ ...r }));
        changes.forEach((change) => {
            const rowIndex = change.rowId as number;
            const columnId = change.columnId as string;
            if (typeof rowIndex !== 'number' || !next[rowIndex]) return;

            if (columnId === ACTION_COLUMN_ID) {
                // Guard: a text edit on the action cell toggles DELETE, never writes a data field.
                next[rowIndex].action = next[rowIndex].action === 'DELETE' ? '' : 'DELETE';
                return;
            }
            if (!(EDITABLE_TEXT_FIELDS as readonly string[]).includes(columnId)) return;
            // Code and name only change on a new row; the price list is always editable.
            if ((columnId === 'insuranceCode' || columnId === 'name') && !next[rowIndex].isNew) return;
            (next[rowIndex] as any)[columnId] = (change.newCell as TextCell).text;
        });
        commitRows(next);
    }, [gridRows, commitRows]);

    return (
        <Box id="insurer-assignment-grid-container" sx={gridContainerSx}>
            <ReactGrid
                rows={gridModel}
                columns={columns}
                onCellsChanged={handleChanges}
                onFocusLocationChanged={onActionFocus}
                onColumnResized={handleColumnResize}
                enableRangeSelection
                enableRowSelection
                stickyTopRows={1}
            />
        </Box>
    );
};

export default InsurerAssignmentGrid;
